import React, { useMemo } from 'react';
import KeyValueTable from './KeyValueTable';

/*
 * AuspiciousPeriodsSection — Section 5 of Pambu Sheet.
 *
 * Rows: Abhijit, Brahma, Amrit Kalam, Nalla Neram (Day + Night, multi-window).
 * Times in HH:MM (no seconds).
 * Active period: green left-border accent on activeRowKey.
 */

function nowInRange(now, start, end) {
  if (!now || !start || !end) return false;
  const toMin = (t) => {
    const parts = t.split(':').map(Number);
    return (parts[0] || 0) * 60 + (parts[1] || 0);
  };
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const startMin = toMin(start);
  const endMin = toMin(end);
  if (endMin >= startMin) {
    return nowMin >= startMin && nowMin < endMin;
  }
  return nowMin >= startMin || nowMin < endMin;
}

function fmtRange(start, end) {
  if (!start || !end) return '—';
  return `${start.substring(0, 5)} — ${end.substring(0, 5)}`;
}

function buildSingleWindow(key, labelTa, labelEn, period, now) {
  if (!period || !period.start) return null;
  return {
    key,
    labelTa,
    labelEn,
    value: fmtRange(period.start, period.end),
    active: nowInRange(now, period.start, period.end),
  };
}

function buildMultiWindow(keyPrefix, labelTa, labelEn, windows, now) {
  if (!windows || windows.length === 0) return null;
  const first = windows[0];
  const subWindows = windows.slice(1);
  const subRows = subWindows.map((w, idx) => ({
    key: `${keyPrefix}_sub_${idx}`,
    labelTa: '',
    labelEn: '',
    value: fmtRange(w.start, w.end),
    active: nowInRange(now, w.start, w.end),
  }));
  return {
    key: keyPrefix,
    labelTa,
    labelEn,
    value: fmtRange(first.start, first.end),
    active: nowInRange(now, first.start, first.end),
    subRows,
  };
}

const AuspiciousPeriodsSection = ({ auspicious, now, lang = 'en' }) => {
  const built = useMemo(() => {
    if (!auspicious) return [];
    const out = [];
    const single = (k, ta, en, period) => {
      const r = buildSingleWindow(k, ta, en, period, now);
      if (r) out.push(r);
    };
    single('abhijit', 'அபிஜித் முகூர்த்தம்', 'Abhijit Muhurta', auspicious.abhijit_muhurta || auspicious.abhijit);
    single('brahma', 'பிரம்ம முகூர்த்தம்', 'Brahma Muhurta', auspicious.brahma_muhurta || auspicious.brahma);
    if (auspicious.amrit_kalam || auspicious.amritKalam) {
      single('amrit', 'அமிர்த காலம்', 'Amrit Kalam', auspicious.amrit_kalam || auspicious.amritKalam);
    }
    const dayWindows = auspicious.nalla_neram_day || auspicious.nallaNeramDay;
    const nightWindows = auspicious.nalla_neram_night || auspicious.nallaNeramNight;
    if (Array.isArray(dayWindows) && dayWindows.length > 0) {
      const r = buildMultiWindow('nalla_day', 'நல்ல நேரம் (பகல்)', 'Nalla Neram (Day)', dayWindows, now);
      if (r) out.push(r);
    }
    if (Array.isArray(nightWindows) && nightWindows.length > 0) {
      const r = buildMultiWindow('nalla_night', 'நல்ல நேரம் (இரவு)', 'Nalla Neram (Night)', nightWindows, now);
      if (r) out.push(r);
    }
    return out;
  }, [auspicious, now]);

  if (built.length === 0) return null;

  let activeKey = null;
  for (const row of built) {
    if (row.active) { activeKey = row.key; break; }
    if (row.subRows) {
      const activeSub = row.subRows.find(s => s.active);
      if (activeSub) { activeKey = activeSub.key; break; }
    }
  }

  return <KeyValueTable rows={built} activeRowKey={activeKey} lang={lang} accentTone="good" />;
};

export default AuspiciousPeriodsSection;
